"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, FileText, Clock, ShieldCheck, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Neighborhood() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="serving-your-neighborhood" data-nav="light" className="relative overflow-hidden bg-background py-14 md:py-20 lg:py-24">
      <div className="container mx-auto max-w-[1240px] px-5 sm:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="relative rounded-2xl border border-border bg-card p-6 shadow-lg sm:p-10 md:p-12 lg:p-16">
          {/* Subtle Decorative Gold Accent Strip */}
          <div className="absolute top-0 left-0 h-full w-1.5 rounded-l-2xl bg-accent" />

          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12">
            {/* Main Content Column */}
            <div className="lg:col-span-7">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-bold uppercase tracking-wider text-secondary-foreground">
                <MapPin className="size-3.5 text-accent" />
                <span>Jonesboro &amp; Clayton County</span>
              </div>

              <h2 className="font-default text-2xl font-bold tracking-tight text-card-foreground sm:text-3xl md:text-4xl">
                Need a Plumber in Your Area?
              </h2>

              <p className="mt-4 font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
                Tell us where you are and what&apos;s happening. Our licensed local technicians are stationed across Jonesboro for fast, 24/7 on-site response. Clear upfront pricing, no hidden dispatch charges, and direct phone help right when you need it.
              </p>

              {/* Trust Indicators */}
              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-semibold text-foreground sm:text-sm">
                <div className="flex items-center gap-1.5">
                  <Clock className="size-4 text-accent" />
                  <span>24/7 Rapid Dispatch</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="size-4 text-accent" />
                  <span>Licensed &amp; Insured</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full bg-accent" />
                  <span>Zero Overtime Surprises</span>
                </div>
              </div>
            </div>

            {/* Action Response Cluster */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:col-span-5 lg:flex-col lg:items-stretch lg:justify-center">
              {/* Primary Direct-Dial Button */}
              <Button asChild className="h-auto rounded-full bg-accent px-7 py-4 text-base font-bold text-accent-foreground shadow-md transition-[filter,box-shadow] duration-200 hover:brightness-105 hover:shadow-lg focus-visible:ring-ring">
                <a href="tel:(470) 847-9076" className="flex items-center justify-center gap-3">
                  <PhoneCall className="size-5 shrink-0 animate-pulse" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              {/* Secondary Service Request Form Link */}
              <Button asChild variant="outline" className="h-auto rounded-full border-border bg-background px-6 py-3.5 text-sm font-semibold text-foreground shadow-xs transition-[background-color,border-color,box-shadow] duration-200 hover:border-primary hover:bg-muted focus-visible:ring-ring">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <FileText className="size-4 shrink-0 text-muted-foreground" />
                  <span>Request Service</span>
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}